import { ProductTypes } from '@/modules/base/util/constants'

interface RelatedSubscription {
  isExpired?: boolean
  billingData?: { store?: { type?: string, subscriptionId?: string } }
}

interface CreateButtonLinkProps {
  typeOfSubscription: string
  serial?: string
  relatedSubscription?: RelatedSubscription
  upgradeLink?: string
  value: string
}

// Port of subscription-item.meinbuero.tsx's createButtonLink, shared with the revenue-limit widgets.
export const createButtonLink = ({ typeOfSubscription, serial, relatedSubscription, upgradeLink, value }: CreateButtonLinkProps) => {
  const actualMarketplaceId = relatedSubscription?.billingData?.store?.type === 'web' && relatedSubscription.billingData?.store?.subscriptionId
  if (serial) {
    if (typeOfSubscription === ProductTypes.DOCUMENT && relatedSubscription && !upgradeLink) {
      return ''
    }
    if (typeOfSubscription === ProductTypes.MAIN && relatedSubscription) {
      return upgradeLink || value
    }
    return upgradeLink ? `${upgradeLink}&bdls=${serial}` : `${value}&bdls=${serial}`
  } else if (actualMarketplaceId) {
    return `${value}&subid=${actualMarketplaceId}`
  } else if (!relatedSubscription) {
    return `${value}&subid=new`
  } else {
    return value
  }
}

interface Plan {
  name: string
  sections: { items: { value: { type: string, value: string, metaData: { shopType?: string, type?: string } } }[] }[]
}

// Mirrors buttonEle's shouldRender check in subscription-item.meinbuero.tsx: the button lives in
// a plan's last section, and metaData.type picks new_purchase vs upgrade.
export const getPlanButtonItem = (plan: Plan, relatedSubscription?: RelatedSubscription) => {
  const items = plan?.sections?.at(-1)?.items?.filter(item => item.value?.type === 'button') || []
  const item = items.find(item => {
    const { type } = item.value.metaData || {}
    return (type === 'new_purchase' && (!relatedSubscription || relatedSubscription?.isExpired))
      || (type === 'upgrade' && !!relatedSubscription && !relatedSubscription?.isExpired)
      || !['new_purchase', 'upgrade'].includes(type)
  })
  return item?.value || null
}

// Subscription type names are '<tier>' or '<tier>_<pricing-generation>' (e.g. 's', 's_2021',
// 'xs_2025'). The tier is the leading run of letters, so any pricing generation resolves
// without this file needing an entry added for it.
const tierOf = (type?: string) => type?.match(/^[a-z]+/)?.[0] || null

// Finds the current plan's tier key, then takes whichever plan sits right after it in the
// product's own plans list.
export const getNextPlan = (plans: Plan[] = [], subscriptionPlanOrder: { name: string, key: string }[] = [], currentType: string) => {
  const currentTier = tierOf(currentType)
  const currentKey = currentTier && subscriptionPlanOrder.find(order => tierOf(order.name) === currentTier)?.key
  const currentIndex = currentKey ? plans.findIndex(plan => plan.name === currentKey) : -1
  if (currentIndex === -1) return null
  return plans[currentIndex + 1] || null
}
